// b14: jordfukten i Stugkoll. Kortet Jordfukt på Hälsa för ett hus: en rad per givare med namnet,
// fukten i %, en liten kurva för de senaste 7 dygnen och batteriet, och gränserna. Under
// Inställningar → Jordgivarna byter ägaren namnet och gränserna (torrt och blött). Namnet kommer
// från Home Assistant tills ägaren ger ett eget.

import { type Jordgivare, type Platsinstallningar, sedan, tal } from "@batkoll/gemensam";
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { api, Felsvar } from "../api.ts";
import { Kort, Rad } from "../komponenter.tsx";

const visaTal = (v: number | null | undefined) =>
  v === null || v === undefined ? "" : String(v).replace(".", ",");
const lasTal = (s: string) => (s.trim() ? Number(s.trim().replace(",", ".")) : Number.NaN);

/** Batteriet: lågt, spänningen eller nivån. */
function batteri(b: Jordgivare["batteri"]): string {
  const delar: string[] = [];
  if (b.volt !== null) delar.push(`${tal(b.volt, 2)} V`);
  if (b.procent !== null) delar.push(`${tal(b.procent, 0)} %`);
  if (b.lagt === true) return ["lågt", ...delar].join(", ");
  if (delar.length === 0) return b.lagt === false ? "ok" : "finns inte";
  return delar.join(", ");
}

/** En liten kurva för fukten de senaste 7 dygnen, 0–100 %. */
function Minikurva(props: { g: Jordgivare; nu: number }) {
  const k = props.g.kurva;
  if (k.length < 2) return <span class="liten">ingen kurva än</span>;
  const b = 120;
  const h = 32;
  const fran = props.nu - 7 * 86_400_000;
  const x = (t: number) => Math.max(0, Math.min(b, ((t - fran) / (7 * 86_400_000)) * b));
  const y = (f: number) => h - (Math.max(0, Math.min(100, f)) / 100) * h;
  const punkter = k.map((p) => `${x(p.tid).toFixed(1)},${y(p.fukt).toFixed(1)}`).join(" ");
  return (
    <svg
      class="minikurva"
      viewBox={`0 0 ${b} ${h}`}
      width={b}
      height={h}
      role="img"
      aria-label={`Jordfukten de senaste 7 dygnen för ${props.g.namn}: från ${tal(k[0]?.fukt ?? 0, 0)} % till ${tal(k.at(-1)?.fukt ?? 0, 0)} %`}
    >
      <line class="grans" x1="0" x2={b} y1={y(props.g.torr)} y2={y(props.g.torr)} />
      <line class="grans" x1="0" x2={b} y1={y(props.g.blot)} y2={y(props.g.blot)} />
      <polyline points={punkter} />
    </svg>
  );
}

const LAGE: Record<NonNullable<Jordgivare["lage"]>, string> = {
  torr: "torr",
  blot: "blöt",
  ok: "lagom",
};

/** Kortet Jordfukt på Hälsa. */
export function Jordfuktkort(props: { g: Jordgivare[]; nu: number }) {
  if (props.g.length === 0) return null;
  const torr = props.g.some((g) => g.lage === "torr");
  return (
    <Kort rubrik="Jordfukt" ton={torr ? "varning" : undefined}>
      {props.g.map((g) => (
        <section key={g.kanal} class="jordgivare" aria-label={g.namn}>
          <h3>{g.namn}</h3>
          <div class="jordrad">
            <div class="stort">{g.fukt === null ? "–" : `${tal(g.fukt, 0)} %`}</div>
            <Minikurva g={g} nu={props.nu} />
          </div>
          <Rad namn="Läget">
            {g.lage ? LAGE[g.lage] : "finns inte"}
            {g.tid !== null ? `, ${sedan(g.tid, props.nu)}` : ""}
          </Rad>
          <Rad namn="Batteriet">{batteri(g.batteri)}</Rad>
          <p class="liten">
            Torrt under {tal(g.torr, 0)} %, blött över {tal(g.blot, 0)} %.
          </p>
        </section>
      ))}
      <p class="liten">
        En Varning när jorden har varit torr i mer än 6 timmar, och en Info när den har varit blöt i
        mer än ett dygn. En påminnelse om att vattna kommer när jorden är torr och prognosen visar
        högst 2 mm regn det närmaste dygnet, högst en gång per givare och dygn, också i läget Borta.
        Båtkoll vattnar inte.
      </p>
    </Kort>
  );
}

type Spara = (
  sektion: string,
  gor: () => Promise<Platsinstallningar>,
  ok?: string,
) => Promise<boolean>;

/** Inställningar → Jordgivarna: namnet och gränserna per givare. */
export function Jordgivardel(props: {
  s: Platsinstallningar;
  spara: Spara;
  beskedrad: ComponentChildren;
}) {
  const lista = props.s.jordgivare ?? [];
  const [falt, setFalt] = useState<Record<string, { namn: string; torr: string; blot: string }>>(
    Object.fromEntries(
      lista.map((g) => [
        g.kanal,
        {
          namn: g.namn ?? "",
          torr: g.torr.installd ? visaTal(g.torr.varde) : "",
          blot: g.blot.installd ? visaTal(g.blot.varde) : "",
        },
      ]),
    ),
  );
  if (lista.length === 0) return null;
  const id = props.s.plats.id;

  function spara(e: Event) {
    e.preventDefault();
    void props.spara(
      "jordgivare",
      async () => {
        const jordgivare: Record<
          string,
          { namn: string | null; torr: number | null; blot: number | null }
        > = {};
        for (const g of lista) {
          const f = falt[g.kanal];
          if (!f) continue;
          const tal1 = (v: string, namn: string) => {
            if (!v.trim()) return null;
            const x = lasTal(v);
            if (!Number.isFinite(x)) throw new Felsvar(`${namn} ska vara ett tal.`, 400);
            return x;
          };
          jordgivare[g.kanal] = {
            namn: f.namn.trim() ? f.namn.trim() : null,
            torr: tal1(f.torr, "Gränsen för torrt"),
            blot: tal1(f.blot, "Gränsen för blött"),
          };
        }
        return api.andraPlats(id, { jordgivare });
      },
      "Jordgivarna är sparade.",
    );
  }

  const andra = (kanal: string, x: Partial<{ namn: string; torr: string; blot: string }>) =>
    setFalt({ ...falt, [kanal]: { ...(falt[kanal] ?? { namn: "", torr: "", blot: "" }), ...x } });

  return (
    <Kort rubrik="Jordgivarna">
      <form class="formular" onSubmit={spara}>
        {lista.map((g) => {
          const f = falt[g.kanal] ?? { namn: "", torr: "", blot: "" };
          const rubrik = `Kanal ${g.kanal.slice(5)}`;
          return (
            <fieldset key={g.kanal}>
              <legend>
                {rubrik}
                {g.haNamn ? ` (${g.haNamn} i Home Assistant)` : ""}
              </legend>
              <label>
                {`${rubrik}, namn`}
                <input
                  autoComplete="off"
                  value={f.namn}
                  placeholder={g.haNamn ?? rubrik}
                  onInput={(e) => andra(g.kanal, { namn: e.currentTarget.value })}
                />
              </label>
              <label>
                {`${rubrik}, torrt under (%)`}
                <input
                  inputMode="decimal"
                  autoComplete="off"
                  value={f.torr}
                  placeholder={visaTal(g.torr.forval)}
                  onInput={(e) => andra(g.kanal, { torr: e.currentTarget.value })}
                />
              </label>
              <label>
                {`${rubrik}, blött över (%)`}
                <input
                  inputMode="decimal"
                  autoComplete="off"
                  value={f.blot}
                  placeholder={visaTal(g.blot.forval)}
                  onInput={(e) => andra(g.kanal, { blot: e.currentTarget.value })}
                />
              </label>
            </fieldset>
          );
        })}
        <p class="liten">
          Tomt namn: namnet från Home Assistant. Tomma gränser: förvalen, torrt under 25 % och blött
          över 80 %.
        </p>
        {props.beskedrad}
        <div class="knapprad">
          <button class="knapp-huvud" type="submit">
            Spara jordgivarna
          </button>
        </div>
      </form>
    </Kort>
  );
}
